import { useEffect, useRef, useState } from 'react'
import type { LocalProfile } from '../services/hubProfile.ts'
import type { AppSettings } from '../config/settings.ts'
import { DEFAULT_AVATAR_EMOJI } from '../config/profile.ts'
import bg from '../assets/bg2.webp'
import titleLogo from '../assets/title_logo.webp'
import { useStamina } from '../hooks/useStamina.ts'
import { HelpModal } from './HelpModal.tsx'
import { ProfileEditModal } from './ProfileEditModal.tsx'
import { SettingsModal } from './SettingsModal.tsx'
import { StaminaDisplay } from './StaminaDisplay.tsx'
import { loadSettings, updateSettings } from '../services/settings.ts'
import { setBgmMuted, setSeMuted } from '../services/volume.ts'
import { resumeAudioContext } from '../services/audioContext.ts'
import { playSe } from '../services/se.ts'
import { t } from '../i18n/translations.ts'
import { addToHomeScreen, canAddToHomeScreen } from '../services/liff.ts'
import { trackEvent } from '../services/analytics.ts'
import { ArcadeMoreButton } from './ArcadeMoreButton.tsx'

interface Props {
  highscore: number
  profile: LocalProfile | null
  onStart: () => void
  onRanking: () => void
  onProfileUpdated: (profile: LocalProfile) => void
  onSettingsChange?: (settings: AppSettings) => void
  /**
   * 音を OFF → ON にした直後に呼ばれる。autoplay ポリシーで paused のまま
   * 残っている BGM を、このユーザージェスチャ内で再生開始させるためのフック。
   */
  onSoundEnabled?: () => void
  backRequestId?: number
  onBackUnhandled?: () => void
}

/**
 * タイトル画面（正方形ビューポート対応レイアウト）。
 *
 * Sweeper の Overlay 構造（`luckyanimal-sweeper/src/game/sweeper/components/Overlay.tsx`）
 * を参考に、縦長・正方形（LINE Mini App, iPad 分割画面等）いずれでも破綻しない
 * 3 ゾーン構成に刷新:
 *   1. 右上アイコン（🔊 / ？ / ⚙）
 *   2. 上部プロフィールバッジ
 *   3. ロゴ + スタート + ライフ/ボーナス（縦中央寄せ / 縦方向に収まるよう min-h:0）
 *
 * 旧実装にあったオンライン対戦 / ともだちを誘う / VS 表示は将来のアップデート要素として
 * 一旦削除（UI から導線を閉じる）。
 */
export function TitleScreen({
  highscore,
  profile,
  onStart,
  onRanking,
  onProfileUpdated,
  onSettingsChange,
  onSoundEnabled,
  backRequestId = 0,
  onBackUnhandled,
}: Props) {
  const [helpOpen, setHelpOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // BGM / SE は個別に保存されるが、タイトルのアイコンは「片方でも鳴るなら ON」表示。
  const [soundOn, setSoundOn] = useState(() => {
    const s = loadSettings()
    return !s.bgmMuted || !s.seMuted
  })
  const handledBackRequestId = useRef(0)

  const { canPlay } = useStamina()

  const avatarSrc = profile?.pictureUrl ?? null
  const showAddToHome = canAddToHomeScreen()

  useEffect(() => {
    if (backRequestId === 0) return
    if (handledBackRequestId.current === backRequestId) return
    handledBackRequestId.current = backRequestId
    if (settingsOpen) {
      setSettingsOpen(false)
      return
    }
    if (profileOpen) {
      setProfileOpen(false)
      return
    }
    if (helpOpen) {
      setHelpOpen(false)
      return
    }
    onBackUnhandled?.()
  }, [backRequestId, helpOpen, onBackUnhandled, profileOpen, settingsOpen])

  const handleStart = () => {
    if (!canPlay) return
    onStart()
  }

  /**
   * タイトルの 🔊 / 🔇 トグル。BGM と SE をまとめて切り替える。
   *
   * ON にするときの順序が重要:
   *   1. ユーザージェスチャ内で同期的に AudioContext を resume（iOS の autoplay 対策）
   *   2. 永続化 + 実行時ストア（volume.ts）へ反映
   *   3. paused のままの BGM を再生開始 + 確認用の SE を 1 回鳴らす
   */
  const handleToggleSound = () => {
    const nextOn = !soundOn
    if (nextOn) resumeAudioContext()

    const next = updateSettings({ bgmMuted: !nextOn, seMuted: !nextOn })
    setBgmMuted(next.bgmMuted)
    setSeMuted(next.seMuted)
    setSoundOn(nextOn)
    onSettingsChange?.(next)

    if (nextOn) {
      onSoundEnabled?.()
      // 「音が出た」ことをその場で伝えるフィードバック（既存の着手音を流用）。
      playSe('place-player')
    }
  }

  // 設定モーダル側で BGM / SE を変えたときも、タイトルのアイコン表示を追随させる。
  const handleSettingsChange = (next: AppSettings) => {
    setSoundOn(!next.bgmMuted || !next.seMuted)
    onSettingsChange?.(next)
  }

  const handleAddToHome = async () => {
    const created = await addToHomeScreen()
    trackEvent('add_to_home', { created })
  }

  return (
    <div
      className="screen screen--title"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="title-overlay">
        {/* --- 右上アイコン（🔊 / ？ / ⚙） --- */}
        <div className="title-top-icons">
          <button
            type="button"
            className="btn--icon title-top-icons__btn"
            onClick={handleToggleSound}
            aria-pressed={soundOn}
            aria-label={soundOn ? t('soundDisableAria') : t('soundEnableAria')}
          >
            <span aria-hidden="true">{soundOn ? '🔊' : '🔇'}</span>
          </button>
          <button
            type="button"
            className="btn--icon title-top-icons__btn"
            onClick={() => setHelpOpen(true)}
            aria-label={t('helpAria')}
          >
            <span aria-hidden="true">?</span>
          </button>
          <button
            type="button"
            className="btn--icon title-top-icons__btn"
            onClick={() => setSettingsOpen(true)}
            aria-label={t('settingsAria')}
          >
            <span aria-hidden="true">⚙</span>
          </button>
        </div>

        {/* --- プロフィール（画面上部、小さめ） --- */}
        <div className="title-overlay__top">
          <button
            type="button"
            className="title-profile-card title-profile-card--compact"
            onClick={() => setProfileOpen(true)}
            aria-label={t('profileEdit')}
          >
            <span className="title-profile-card__avatar">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={profile?.displayName ?? t('player')}
                />
              ) : (
                <span className="title-profile-card__avatar-fallback">
                  {DEFAULT_AVATAR_EMOJI}
                </span>
              )}
            </span>
            <span className="title-profile-card__name">
              {profile?.displayName ?? t('player')}
            </span>
            <span className="title-profile-card__edit" aria-hidden="true">
              ✏️
            </span>
          </button>
          {highscore > 0 && (
            <div className="title-highscore title-highscore--top">
              {t('highscore')}: {highscore.toLocaleString()}
            </div>
          )}
        </div>

        {/* --- ロゴ + メイン CTA + スタミナ --- */}
        <div className="title-overlay__main">
          <div className="title-overlay__logo">
            <img
              src={titleLogo}
              alt={t('appTitle')}
              className="title-logo-img"
            />
          </div>

          <div className="title-overlay__actions">
            <button
              type="button"
              className="btn btn--primary btn--large title-start-btn"
              onClick={handleStart}
              disabled={!canPlay}
            >
              {canPlay ? t('start') : t('staminaEmpty')}
            </button>
            <button
              type="button"
              className="btn btn--secondary title-ranking-btn"
              onClick={onRanking}
            >
              🏆 {t('ranking')}
            </button>
            <StaminaDisplay showRecoverButton />
          </div>
        </div>
      </div>

      {showAddToHome && (
        <button
          type="button"
          className="title-add-home"
          onClick={handleAddToHome}
          title="ホーム画面に追加"
          aria-label="ホーム画面に追加"
        >
          <span className="title-add-home__icon" aria-hidden="true">
            <img src="/icon.png" alt="" />
          </span>
          <span className="title-add-home__label">
            ホームに<br />追加する
          </span>
        </button>
      )}

      <ArcadeMoreButton />

      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}

      <ProfileEditModal
        open={profileOpen}
        currentProfile={profile}
        onSaved={onProfileUpdated}
        onClose={() => setProfileOpen(false)}
      />

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSettingsChange={handleSettingsChange}
        onSoundEnabled={onSoundEnabled}
      />
    </div>
  )
}
